// src/Pages/ComplainFollowUp.js
import React, { useEffect, useState } from "react";
import axios from "axios";

// MUI Components
import { Container, Paper, Box, Grid, Typography, Card, CardContent, Button, IconButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme,} from "@mui/material";

// React Icons
import { MdExpandMore } from "react-icons/md";
import { HiOutlineClock, HiOutlineCalendar, HiOutlinePlusCircle, HiOutlineXCircle,} from "react-icons/hi";
import { FaHourglassHalf } from "react-icons/fa6";
import { FaCalendarAlt } from "react-icons/fa";

// MUI History icon
import HistoryIcon from "@mui/icons-material/History";
import FormattedDate from "../components/FormattedDate";
import DateInput from "../components/DateInput";
import FloatingLabelInput from "../components/FloatingLabelInput";
import Dropdown from "../components/Dropdown";
import ComplainForm from "./ComplainForm";
import ComplainHistoryManagement from "./ComplainHistoryManagement";

// Base API path and category list for enquiries.
// const path = "http://103.251.218.254:3100";
const path = "http://192.168.1.248:3107";
const categories = ["Missed", "Today", "Upcomming", "Delayed", "Failed"];

// Helper function to split records for a team leader.
// Helper function to split records for a team leader.
const splitRecordsForTeamLeader = (records, userId) => {
  // Ensure records is an array
  const recs = Array.isArray(records) ? records : [];

  const selfRecords = recs.filter((record) => record.userId === userId);
  const teamRecords = recs.filter((record) => record.userId !== userId);
  return { self: selfRecords, team: teamRecords };
};

const ComplainFollowUp = ({ loggedInUser, setLoggedInUser }) => {
  // -------------------- STATE DECLARATIONS --------------------
  // For regular users: dataSets[category] is an array.
  // For team leaders: dataSets[category] is an object { self: [], team: [] }.
  const [dataSets, setDataSets] = useState({});
  const [visibleTable, setVisibleTable] = useState(null);
  const [recordCounts, setRecordCounts] = useState({});
  const [offsets, setOffsets] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [ComplaintNumber, setComplaintNumber] = useState("");

  // Pagination state.
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayedRecords, setDisplayedRecords] = useState(0);

  // Individual search field state variables.
  const [searchInquiryDate, setSearchInquiryDate] = useState("");
  const [searchComplaintNumber, setSearchComplaintNumber] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchAssign, setSearchAssign] = useState("");
  const [searchCompanyName, setSearchCompanyName] = useState("");
  const [searchContactPerson, setSearchContactPerson] = useState("");
  const [searchMobileNo, setSearchMobileNo] = useState("");

  // Dropdown options for filtering.
  const [statusOptions, setStatusOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  // Flag to indicate whether a search is active.
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Modal states.
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showEnquiryFormModal, setShowComplaintFormModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historycomplainId, setHistorycomplainId] = useState(null);

  // Responsive handling using theme and media query.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // -------------------- EFFECTS & RESPONSIVE HANDLING --------------------
  // Update window width state on resize.
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // -------------------- DATA FETCHING FUNCTIONS --------------------
  /**
   * fetchEnquiries
   * ----------------
   * Fetches enquiries from the backend based on the given category, offset, and filter values.
   * @param {string} category - The enquiry category (e.g., "Today").
   * @param {number} currentOffset - The offset for pagination.
   * @param {object|null} filtersOverride - Optional. If provided, these filters override the current search states.
   * @returns {Array} - Array of fetched enquiry records.
   */
  const fetchEnquiries = async (category, currentOffset = 0, filtersOverride = null) => {
    try {
      // Build filters object.
      let filtersToSend = {};
      if (filtersOverride === null) {
        if (searchInquiryDate.trim() !== "") {
          filtersToSend.inquiryDate = searchInquiryDate.trim();
        }
        if (searchComplaintNumber.trim() !== "") {
          filtersToSend.ComplaintNumber = searchComplaintNumber.trim();
        }
        if (searchStatus.trim() !== "") {
          filtersToSend.status = searchStatus.trim();
        }
        if (searchAssign.trim() !== "") {
          filtersToSend.assign = searchAssign.trim();
        }
        if (searchCompanyName.trim() !== "") {
          filtersToSend.companyName = searchCompanyName.trim();
        }
        if (searchContactPerson.trim() !== "") {
          filtersToSend.contactPerson = searchContactPerson.trim();
        }
        if (searchMobileNo.trim() !== "") {
          filtersToSend.mobileNo = searchMobileNo.trim();
        }
      } else {
        filtersToSend = filtersOverride;
      }
  
      // Build API query parameters.
      const params = { category, offset: currentOffset, limit: 10, userId: loggedInUser.userId, role: loggedInUser.userCategory,companyId: loggedInUser.companyId, ...filtersToSend, };
  
      const res = await axios.get(`${path}/api/get-complain`, { params });
      console.log("Fetched enquiries:", res.data);
      // The backend response should include "complaints" and "totalRecords".
      // Update global pagination if needed.
      if (res.data.total !== undefined) {
        if (currentOffset === 0) {
          setDisplayedRecords(res.data.complaints.length);
        } else {
          setDisplayedRecords(prev => prev + res.data.complaints.length);
        }
      }
      return { complaints: res.data.complaints || [], totalRecords: res.data.totalRecords || 0 };
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      return { complaints: [], totalRecords: 0 };
    }
  };

  // -------------------- INITIAL DATA LOAD & COUNTS --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const newData = {};
        const newOffsets = {};
        const newRecordCounts = {};
        // Loop through each category in the array.
        for (const cat of categories) {
          // Normalize the category key: if "Upcomming", use "Upcoming"
          const normalizedCat = cat === "Upcomming" ? "Upcoming" : cat;
          const result = await fetchEnquiries(cat, 0);
          const records = result.complaints;
          const total = result.totalRecords;
          newRecordCounts[normalizedCat] = total;  // Save the total count per normalized category
          if (loggedInUser.userCategory === "Team Leader") {
            const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
            newData[normalizedCat] = splitData;
            newOffsets[normalizedCat] = { self: splitData.self.length, team: splitData.team.length };
          } else {
            newData[normalizedCat] = records;
            newOffsets[normalizedCat] = records.length;
          }
        }
        setDataSets(newData);
        setOffsets(newOffsets);
        setRecordCounts(newRecordCounts);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchData();
  }, [loggedInUser]);

  // -------------------- FETCH DROPDOWN OPTIONS WHEN SEARCH MODAL OPENS --------------------
  useEffect(() => {
    if (showSearchModal) {
      axios
        .get(`${path}/api/status`)
        .then((res) =>
          setStatusOptions(Array.isArray(res.data.data) ? res.data.data : [])
        )
        .catch((error) => console.error("Error fetching status:", error));
      axios
        .get(`${path}/api/users`, {
          params: { companyId: loggedInUser.companyId }
        })
        .then((res) =>
          setUsersOptions(Array.isArray(res.data.data) ? res.data.data : [])
        )
        .catch((error) => console.error("Error fetching users:", error));
    }
  }, [showSearchModal]);

  // -------------------- FETCH THE LATEST INQUIRY NUMBER ON MOUNT --------------------
  useEffect(() => {
    const fetchComplaintNumber = async () => {
      try {
        const res = await axios.get(`${path}/api/getInqNumber`);
        setComplaintNumber(res.data.ComplaintNumber);
      } catch (error) {
        console.error("Error fetching inquiry number:", error);
      }
    };
    fetchComplaintNumber();
  }, []);

  // -------------------- EVENT HANDLERS --------------------
  /**
   * toggleTable
   * -----------
   * Clears individual search states, deactivates search mode, and toggles the visible table.
   * Also closes the search modal.
   * @param {string} type - The selected enquiry category.
   */
  const toggleTable = (type) => {
    // Clear search filters.
    setSearchInquiryDate("");
    setSearchComplaintNumber("");
    setSearchStatus("");
    setSearchAssign("");
    setSearchCompanyName("");
    setSearchContactPerson("");
    setSearchMobileNo("");
    setIsSearchActive(false);
  
    // Normalize the type: if type is "Upcomming", set visibleTable to "Upcoming"
    const normalizedType = type === "Upcomming" ? "Upcoming" : type;
    setVisibleTable(normalizedType);
    setShowSearchModal(false);
  };

  // Individual search field handlers.
  const handleSearchInquiryDateChange = (e) => setSearchInquiryDate(e.target.value);
  const handleSearchComplaintNumberChange = (e) => setSearchComplaintNumber(e.target.value);
  const handleSearchCompanyNameChange = (e) => setSearchCompanyName(e.target.value);
  const handleSearchContactPersonChange = (e) => setSearchContactPerson(e.target.value);
  const handleSearchMobileNoChange = (e) => setSearchMobileNo(e.target.value);

  // Helper: Return a color based on priority value.
  const getPriorityColor = (priority) => {
    if (!priority) return "gray";
    switch (priority.toLowerCase()) {
      case "high":
        return "#d32f2f"; // red
      case "medium":
        return "#fbc02d"; // amber
      case "low":
        return "#388e3c"; // green
      default:
        return "gray";
    }
  };

  /**
   * handleModalSearch
   * -----------------
   * Triggered when the Search button in the search modal is clicked.
   * It fetches enquiries based on the current filter values and updates the displayed data.
   */
  const handleModalSearch = async () => {
    if (visibleTable) {
      const records = await fetchEnquiries(visibleTable, 0);
      if (loggedInUser.userCategory === "Team Leader") {
        const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
        setDataSets((prev) => ({ ...prev, [visibleTable]: splitData }));
      } else {
        setDataSets((prev) => ({ ...prev, [visibleTable]: records }));
      }
      setIsSearchActive(true);
      setShowSearchModal(false);
    }
  };

  /**
   * handleCloseSearchModal (Reset Search)
   * ---------------------------------------
   * Clears all search filters and reloads the enquiries for the visible category exactly as initially loaded.
   * Uses an override of filters (an empty object) to ensure no filters are applied.
   */
  const handleCloseSearchModal = async () => {
    // 1️⃣ Clear all search filters
    setSearchInquiryDate("");
    setSearchComplaintNumber("");
    setSearchStatus("");
    setSearchAssign("");
    setSearchCompanyName("");
    setSearchContactPerson("");
    setSearchMobileNo("");
    setIsSearchActive(false);
  
    // 2️⃣ Refetch data with no filters
    if (visibleTable) {
      const { complaints, totalRecords: total } = await fetchEnquiries(
        visibleTable,
        0,
        {} // empty override => no filters
      );
  
      // 3️⃣ Update dataSets & offsets
      if (loggedInUser.userCategory === "Team Leader") {
        const split = splitRecordsForTeamLeader(complaints, loggedInUser.userId);
        setDataSets((prev) => ({ ...prev, [visibleTable]: split }));
        setOffsets((prev) => ({
          ...prev,
          [visibleTable]: { self: split.self.length, team: split.team.length }
        }));
        setDisplayedRecords(split.self.length + split.team.length);
      } else {
        setDataSets((prev) => ({ ...prev, [visibleTable]: complaints }));
        setOffsets((prev) => ({ ...prev, [visibleTable]: complaints.length }));
        setDisplayedRecords(complaints.length);
      }
  
      // Also update totalRecords if you track it globally
      setTotalRecords(total);
    }
  
    // 4️⃣ Finally, close the modal
    setShowSearchModal(false);
  };

  /**
   * handleRowClick
   * --------------
   * Triggered when a record row is clicked.
   * Sets the selected record and opens the enquiry form modal.
   * @param {object} record - The clicked enquiry record.
   */
  const handleRowClick = (record) => {
    console.log(record);
    setSelectedRecord(record);
    setShowComplaintFormModal(true);
  };

  /**
   * handleOpenHistory
   * -----------------
   * Opens the history modal for the specified enquiry.
   * @param {number|string} complainId - The ID of the enquiry.
   */
  const handleOpenHistory = (complainId) => {
    setHistorycomplainId(complainId);
    setShowHistoryModal(true);
  };

  /**
   * handleShowMore
   * --------------
   * Fetches the next batch of records (pagination) for the currently visible category.
   */
  const handleShowMore = async () => {
    if (!visibleTable) return;
    try {
      const currentOffset = offsets[visibleTable] || 0;
      const newRecords = await fetchEnquiries(visibleTable, currentOffset);
      if (loggedInUser.userCategory === "Team Leader") {
        const splitNew = splitRecordsForTeamLeader(newRecords, loggedInUser.userId);
        setDataSets((prev) => ({
          ...prev,
          [visibleTable]: {
            self: [...(prev[visibleTable]?.self || []), ...splitNew.self],
            team: [...(prev[visibleTable]?.team || []), ...splitNew.team],
          },
        }));
      } else {
        if (newRecords.length > 0) {
          setDataSets((prev) => ({ ...prev, [visibleTable]: [...prev[visibleTable], ...newRecords] }));
        }
      }
      setDisplayedRecords((prev) => prev + newRecords.length);
    } catch (error) {
      console.error("Error fetching more records:", error);
    }
  };

  // -------------------- RENDER FUNCTIONS --------------------
  /**
   * HL_FN_RenderDesktopTable
   * --------------------------
   * Renders the enquiries in a desktop-friendly table view.
   * @param {Array} data - The array of enquiry records to render.
   */
  const HL_FN_RenderDesktopTable = (data) => (
    <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%", boxShadow: 3 }}>
      <Table>
        <TableHead sx={{ backgroundColor: "grey.300" }}>
          <TableRow>
            <TableCell align="center">#</TableCell>
            <TableCell>Complain No</TableCell>
            <TableCell>Complain Date</TableCell>
            <TableCell>Follow-Up Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Client Name</TableCell>
            <TableCell>Contact Person</TableCell>
            <TableCell>Mobile No</TableCell>
            <TableCell>Email Id</TableCell>
            <TableCell>Assign</TableCell>
            <TableCell align="center">History</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((record, index) => {
            const priorityColor = getPriorityColor(record.priority);
            return (
              <TableRow
                key={record.complainId}
                hover
                onClick={() => handleRowClick(record)}
                sx={{
                  backgroundColor: index % 2 === 0 ? "white" : "grey.50",
                  cursor: "pointer",
                }}
              >
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell>{record.complainNumber}</TableCell>
                <TableCell>
                  <FormattedDate dateString={record.complainDate} />
                </TableCell>
                <TableCell>
                  <FormattedDate dateString={record.LastFollowUpDate} />
                </TableCell>
                <TableCell>{record.statusName}</TableCell>
                <TableCell sx={{ color: priorityColor, fontWeight: "bold" }}>
                  {record.priority}
                </TableCell>
                <TableCell>{record.clientname}</TableCell>
                <TableCell>{record.clientContact}</TableCell>
                <TableCell>{record.mobileNo}</TableCell>
                <TableCell>{record.emailId}</TableCell>
                <TableCell>{record.employeeName || "Unassigned"}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenHistory(record.complainId);
                    }}
                  >
                    <HistoryIcon sx={{ color: "primary.main" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * HL_FN_RenderMobileCard
   * ------------------------
   * Renders the enquiries in a mobile-friendly card view.
   * @param {Array} data - The array of enquiry records to render.
   */
  const HL_FN_RenderMobileCard = (data) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: 2 }}>
      {data.map((record, index) => {
        const priorityColor = getPriorityColor(record.priority);
        return (
          <Card
            key={record.complainId}
            variant="outlined"
            sx={{ position: "relative", cursor: "pointer", boxShadow: 2 }}
            onClick={() => handleRowClick(record)}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenHistory(record.complainId);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "white",
                "&:hover": { backgroundColor: "grey.100" },
              }}
            >
              <HistoryIcon sx={{ color: "primary.main" }} />
            </IconButton>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                #{index + 1} - {record.complainNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Complain Date:</strong>{" "}
                <FormattedDate dateString={record.complainDate} />
              </Typography>
              <Typography variant="body2">
                <strong>Follow-Up:</strong>{" "}
                <FormattedDate dateString={record.LastFollowUpDate} />
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {record.statusName}
              </Typography>
              <Typography variant="body2">
                <strong>Priority:</strong>{" "}
                <span style={{ color: priorityColor, fontWeight: "bold" }}>
                  {record.priority}
                </span>
              </Typography>
              <Typography variant="body2">
                <strong>Client Name:</strong> {record.clientname}
              </Typography>
              <Typography variant="body2">
                <strong>Contact Person:</strong> {record.clientContact}
              </Typography>
              <Typography variant="body2">
                <strong>Mobile No:</strong> {record.mobileNo}
              </Typography>
              <Typography variant="body2">
                <strong>Email Id:</strong> {record.emailId}
              </Typography>
              <Typography variant="body2">
                <strong>Assign:</strong> {record.employeeName || "Unassigned"}
              </Typography>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  /**
   * FnRenderTable
   * -------------
   * Determines whether to render the desktop table or mobile card view based on window width.
   * @param {object} param0 - Contains the data array to be rendered.
   * @returns The rendered table or card view.
   */
  const FnRenderTable = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 150 }}> <Typography variant="h5" color="text.secondary"> No records found. </Typography>
        </Box>
      );
    }
    return windowWidth < 1024 ? HL_FN_RenderMobileCard(data) : HL_FN_RenderDesktopTable(data);
  };

  // For team leaders, merge self and team enquiries into one array.
  let mergedComplaint = [];
  if (visibleTable && loggedInUser.userCategory === "Team Leader") {
    mergedComplaint = [ ...(dataSets[visibleTable]?.self || []), ...(dataSets[visibleTable]?.team || []), ];
  }

  // -------------------- COMPONENT RENDER --------------------
  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ textDecoration: "underline" }}> Complaint Follow-Up </Typography>

      {/* MASTER CARDS SECTION */}
      <Box
        sx={{ display: "grid", gap: { xs: 1.5, sm: 3, md: 3 }, my: 3,
          gridTemplateColumns: {
            xs: "1fr",            // Mobile: Single column
            sm: "repeat(2, 1fr)", // Small devices
            md: "repeat(3, 1fr)", // Medium
            lg: "repeat(5, 1fr)", // Large
          },
        }}
      >
        {categories.map((type, index) => {
          const colors = ["#FACC15", "#4ADE80", "#60A5FA", "#A78BFA", "#F87171"];
          const icons = {
            Missed: <HiOutlineClock style={{ width: 28, height: 28, color: "white" }} />,
            Today: <HiOutlineCalendar style={{ width: 28, height: 28, color: "white" }} />,
            Upcoming: <FaCalendarAlt style={{ width: 28, height: 28, color: "white" }} />,
            Delayed: <FaHourglassHalf style={{ width: 28, height: 28, color: "white" }} />,
            Failed: <HiOutlineXCircle style={{ width: 28, height: 28, color: "white" }} />,
          };

          // Normalize category key: if type is "Upcomming", use "Upcoming"
          const normalizedKey = type === "Upcomming" ? "Upcoming" : type;
          return (
            <Box key={type} onClick={() => toggleTable(type)}
              sx={{
                cursor: "pointer",
                color: "white",
                fontWeight: "bold",
                boxShadow: { xs: 3, sm: 2 },
                borderRadius: 4,
                display: "flex",
                flexDirection: {
                  xs: "row",   // Horizontal for mobile
                  sm: "column",
                },
                alignItems: "center",
                justifyContent: {
                  xs: "space-between", // Space between icon, text, and count
                  sm: "center",
                },
                textAlign: {
                  xs: "left",
                  sm: "center",
                },
                width: "100%",
                height: {
                  xs: 55,      // Reduced height on mobile
                  sm: 110,
                  md: 130,
                  lg: 140,
                },
                transition: "all 0.25s ease-in-out",
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: 6,
                },
                backgroundColor: colors[index % colors.length],
                p: { xs: 1, sm: 2.5, md: 3 },
              }}
            >
              {icons[normalizedKey]}
              <Typography variant="subtitle2" sx={{ mt: 1, fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem", lg: "1.2rem" }, }} >
                {type.toUpperCase()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem", lg: "1rem" }, }} >
                {recordCounts[normalizedKey] || 0}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* TOP SECTION: Search, Count & Show More */}
      {visibleTable && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "space-between", mb: 2, px: { xs: 2, md: 4 }, mt: 2, }} >
          <Typography variant="subtitle1" sx={{ mb: { xs: 2, md: 0 } }}>
            Count:{" "} {loggedInUser.userCategory === "Team Leader" ? `${(dataSets[visibleTable]?.self || []).length + (dataSets[visibleTable]?.team || []).length}` : dataSets[visibleTable]?.length || 0}{" "} / {recordCounts[visibleTable] || 0}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Open search modal to enter filter values */}
            <Button variant="contained" color="success" onClick={() => setShowSearchModal(true)} startIcon={<HiOutlinePlusCircle />} sx={{ height: 40, borderRadius: 1 }} > Search </Button>
            {/* Load more records if available */}
            <Button variant="contained" color="primary" onClick={handleShowMore} disabled={displayedRecords >= totalRecords} sx={{ borderRadius: 1 }} > <Box sx={{ display: "flex", alignItems: "center" }}> Show More <MdExpandMore size={20} style={{ marginLeft: 4 }} /> </Box>
            </Button>
          </Box>
        </Box>
      )}

      {/* SEARCH POPUP MODAL */}
      <Dialog open={showSearchModal} onClose={handleCloseSearchModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ mb: 2 }}>Search Follow-Up</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DateInput label="Inq. Date" name="inquiryDate" value={searchInquiryDate} onChange={handleSearchInquiryDateChange} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <FloatingLabelInput label="Inq. No" name="ComplaintNumber" value={searchComplaintNumber} onChange={handleSearchComplaintNumberChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <FloatingLabelInput label="Client Name" name="companyName" value={searchCompanyName} onChange={handleSearchCompanyNameChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <FloatingLabelInput label="Contact Person" name="contactPerson" value={searchContactPerson} onChange={handleSearchContactPersonChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <FloatingLabelInput label="Mobile No" name="mobileNo" value={searchMobileNo} onChange={handleSearchMobileNoChange} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <Dropdown id="status-dropdown" label="Status" name="status" value={searchStatus} options={Array.isArray(statusOptions) ? statusOptions : []} valueKey="LeadStatus" labelKey="LeadStatus" onChange={(selectedOption) => setSearchStatus(selectedOption.LeadStatus)} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <Dropdown id="assign-dropdown" label="Assign" name="assign" value={searchAssign} options={Array.isArray(usersOptions) ? usersOptions : []} valueKey="employeeName" labelKey="employeeName" onChange={(selectedOption) => setSearchAssign(selectedOption.employeeName)} fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {/* Search button triggers filtering using the current search values */}
          <Button variant="contained" color="primary" onClick={handleModalSearch}> Search </Button>
          {/* Reset button clears filter values and reloads initial records */} <Button variant="contained" color="secondary" onClick={handleCloseSearchModal}> Reset </Button>
        </DialogActions>
      </Dialog>

      {/* Render Complaint */}
      {visibleTable && (
        <Box sx={{ width: "100%", overflowX: "auto", mt: 2 }}>
          {loggedInUser.userCategory === "Team Leader" ? ( <Box> <Box>{FnRenderTable({ data: mergedComplaint })}</Box> </Box>
          ) : (
            <Box>{FnRenderTable({ data: dataSets[visibleTable] })}</Box>
          )}
        </Box>
      )}

      {/* EnquiryForm Modal for Follow-Up Mode */}
      <Dialog open={showEnquiryFormModal} onClose={() => setShowComplaintFormModal(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Button onClick={() => setShowComplaintFormModal(false)} sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "red", color: "white", minWidth: 0, width: 32, height: 32, borderRadius: "50%", "&:hover": { backgroundColor: "darkred" }, }} > &times; </Button>
        </DialogTitle>
        <DialogContent>
          <ComplainForm loggedInUser={loggedInUser} ComplainData={selectedRecord} onClose={() => setShowComplaintFormModal(false)} />
        </DialogContent>
      </Dialog>

      {/* History Management Modal */}
      <ComplainHistoryManagement open={showHistoryModal} onClose={() => setShowHistoryModal(false)} complainId={historycomplainId} maxWidth={isDesktop ? "xl" : "sm"} PaperProps={{ sx: { width: isDesktop ? "900px" : "100%" } }} />
    </Container>
  );
};

export default ComplainFollowUp;